import { HTML_MAX_LENGTH, SUBJECT_MAX_LENGTH, TEXT_MAX_LENGTH } from '../models';

const PLACEHOLDER = /\{\{\s*([^{}]*?)\s*\}\}/g;

/** Names used as {{name}} in the content, in order of first use. */
export function extractPlaceholders(content: string): string[] {
    const names: string[] = [];
    let match: RegExpExecArray | null;
    PLACEHOLDER.lastIndex = 0;
    while ((match = PLACEHOLDER.exec(content || '')) !== null) {
        if (!names.includes(match[1])) {
            names.push(match[1]);
        }
    }
    return names;
}

/** Placeholders not listed in templateVariables. */
export function findUnknownPlaceholders(content: string, allowed: string[]): string[] {
    const allowedSet = new Set(allowed || []);
    return extractPlaceholders(content).filter(name => !allowedSet.has(name));
}

/** Patterns the server refuses in admin-typed HTML. */
const UNSAFE_HTML_RULES: { pattern: RegExp; reason: string }[] = [
    { pattern: /<\s*(script|iframe|object|embed|applet)\b/i, reason: '<script>, <iframe>, <object>, <embed> and <applet> tags are not allowed' },
    { pattern: /<[^>]*\son[a-z]+\s*=/i, reason: 'inline event handlers (such as onclick) are not allowed' },
    { pattern: /=\s*["']?\s*(javascript|vbscript)\s*:/i, reason: 'javascript: and vbscript: links are not allowed' },
    { pattern: /=\s*["']?\s*data\s*:\s*text\/html/i, reason: 'data:text/html links are not allowed' },
];

export function findUnsafeHtml(html: string): string[] {
    return UNSAFE_HTML_RULES.filter(rule => rule.pattern.test(html || '')).map(rule => rule.reason);
}

export interface ContentToValidate {
    subject: string;
    /** Only when the HTML is being sent. */
    html?: string;
    /** Only when the text is being sent. */
    text?: string;
}

/** Same checks the server makes; returns human-readable errors (empty = valid). */
export function validateTemplateContent(content: ContentToValidate, allowed: string[]): string[] {
    const errors: string[] = [];
    const subject = content.subject || '';

    if (!subject.trim()) {
        errors.push('Subject is required.');
    } else if (subject.length > SUBJECT_MAX_LENGTH) {
        errors.push(`Subject can be at most ${SUBJECT_MAX_LENGTH} characters.`);
    }
    if (/[\r\n]/.test(subject)) {
        errors.push('Subject must be a single line.');
    }
    addUnknown(errors, 'Subject', subject, allowed);

    if (content.html !== undefined) {
        if (!content.html.trim()) {
            errors.push('The email body is empty.');
        } else if (content.html.length > HTML_MAX_LENGTH) {
            errors.push(`The email HTML can be at most ${HTML_MAX_LENGTH.toLocaleString()} characters.`);
        }
        addUnknown(errors, 'Email body', content.html, allowed);
        findUnsafeHtml(content.html).forEach(reason => errors.push(`Email body: ${reason}.`));
    }

    if (content.text !== undefined) {
        if (content.text.length > TEXT_MAX_LENGTH) {
            errors.push(`The plain-text version can be at most ${TEXT_MAX_LENGTH.toLocaleString()} characters.`);
        }
        addUnknown(errors, 'Plain-text version', content.text, allowed);
    }
    return errors;
}

function addUnknown(errors: string[], field: string, content: string, allowed: string[]): void {
    const unknown = findUnknownPlaceholders(content, allowed);
    if (unknown.length) {
        errors.push(`${field} uses placeholders that are not allowed: ${unknown.join(', ')}`);
    }
}
